/**
 * Centralized SignalR Service
 * Singleton pattern to manage SignalR connection across all Vue components
 */
import * as signalR from "@microsoft/signalr";

class SignalRService {
  connection = null;
  isConnecting = false;
  isConnected = false;
  connectionPromise = null;

  constructor() {
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.urlApi = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
    this.pingInterval = null;
    // Debug mode - set to true to enable logging
    this.debug = import.meta.env.VITE_SIGNALR_DEBUG === 'true' || false;
    
    // Role-based connection settings
    this.allowedRoles = [0, 1, 6]; // Super Admin, Admin, Operator
    this.excludedPaths = ['FullStream', 'Snippet', 'LoginSnippet', 'Login'];
  }

  /**
   * Log helper - only logs when debug mode is enabled
   * @private
   */
  log(...args) {
    if (this.debug) {
      console.log('[SignalR Service]', ...args);
    }
  }

  /**
   * Error log helper - always logs errors
   * @private
   */
  logError(...args) {
    console.error('[SignalR Service]', ...args);
  }

  /**
   * Check if should connect based on role and path
   * @param {number|string} roleId - User role ID
   * @param {string} pathUrl - Current path URL
   * @returns {boolean}
   */
  shouldConnect(roleId, pathUrl = '') {
    if (roleId === null || roleId === '' || roleId === undefined) 
    {
      return false;
    }
    const roleIdNum = Number.parseInt(roleId);
    const hasValidRole = this.allowedRoles.includes(roleIdNum);
    const isExcludedPath = this.excludedPaths.some(path => pathUrl.includes(path));
    
    this.log(`Role check - roleId: ${roleId}, pathUrl: ${pathUrl}, allowed: ${hasValidRole && !isExcludedPath}`);
    
    return hasValidRole && !isExcludedPath;
  }

  /**
   * Initialize and connect to SignalR hub
   * @param {number|string} roleId - User role ID (optional, skip role check if null)
   * @param {string} pathUrl - Current path URL (optional)
   * @returns {Promise<signalR.HubConnection|null>}
   */
  async connect(roleId = null, pathUrl = '') {
    // Check if should connect based on role and path
    this.log('Attempting to connect with roleId:', roleId, 'and pathUrl:', pathUrl);
    if (!this.shouldConnect(roleId, pathUrl)) {
      this.log('Connection skipped due to role or path restrictions');
      return null;
    }

    // If already connected, return existing connection
    if (this.connection && this.isConnected) {
      this.log('Already connected, reusing connection');
      return this.connection;
    }

    // If connection is in progress, wait for it
    if (this.isConnecting && this.connectionPromise !== null) {
      this.log('Connection in progress, waiting...');
      return this.connectionPromise;
    }

    this.isConnecting = true;
    
    this.connectionPromise = this._createConnection();
    
    try {
      const connection = await this.connectionPromise;
      this.isConnecting = false;
      return connection;
    } catch (error) {
      this.isConnecting = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  /**
   * Internal method to create SignalR connection
   * @private
   */
  async _createConnection() {
    this.log('Starting connection creation...');

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.urlApi}hub`, {
        transport: signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([5000, 5000, 10000, 10000, 15000])
      .build();

    this.connection.serverTimeoutInMilliseconds = 120000; // 2 minutes

    // Handle connection lifecycle events
    this.connection.onclose((error) => {
      this.log('Connection closed', error);
      this.isConnected = false;
      this._notifyHandlers('connectionClosed', error);
    });

    this.connection.onreconnecting((error) => {
      this.log('Reconnecting...', error);
      this._notifyHandlers('reconnecting', error);
    });

    this.connection.onreconnected((connectionId) => {
      this.log('Reconnected. Connection ID:', connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this._notifyHandlers('reconnected', connectionId);
    });

    try {
      await this.connection.start();
      this.log('Connected! Connection ID:', this.connection.connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this._registerPendingHandlers();
      this._startPing();
      return this.connection;
    } catch (err) {
      this.logError('Connection Error:', err);
      this.isConnected = false;
      throw err;
    }
  }

  /**
   * Start ping interval to keep connection alive
   * @private
   */
  _startPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    this.pingInterval = setInterval(async () => {
      try {
        if (this.connected) {
          await this.connection.invoke("Ping");
        }
      } catch (error) {
        this.log('Ping error:', error);
        // Try to reconnect if ping fails, but only when the connection really is
        // Disconnected - starting one that is Connecting/Reconnecting throws
        if (this.connection && this.connection.state === signalR.HubConnectionState.Disconnected) {
          try {
            await this.connection.start();
            this.isConnected = true;
            this._registerPendingHandlers();
          } catch (reconnectError) {
            this.logError('Reconnection failed:', reconnectError);
          }
        }
      }
    }, 30000); // 30 seconds
  }

  /**
   * Stop ping interval
   * @private
   */
  _stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect() {
    this._stopPing();
    if (this.connection) {
      try {
        await this.connection.stop();
        this.log('Disconnected!');
      } catch (error) {
        this.logError('Error stopping connection:', error);
      }
      this.isConnected = false;
      this.connection = null;
      this.connectionPromise = null;
    }
  }

  /**
   * Register an event handler for SignalR events
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler (e.g., component name)
   * @param {Function} callback - The callback function
   */
  on(eventName, handlerId, callback) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Map());
    }

    // Safe to call on every registration - _bindEvent replaces the dispatcher
    this._bindEvent(eventName);

    this.eventHandlers.get(eventName).set(handlerId, callback);
  }

  /**
   * Unregister an event handler
   * @param {string} eventName - The name of the SignalR event
   * @param {string} handlerId - Unique identifier for the handler
   */
  off(eventName, handlerId) {
    if (this.eventHandlers.has(eventName)) {
      this.eventHandlers.get(eventName).delete(handlerId);
    }
  }

  /**
   * Unregister all event handlers for a specific handler ID (e.g., when component unmounts)
   * @param {string} handlerId - Unique identifier for the handler
   */
  offAll(handlerId) {
    for (const [, handlers] of this.eventHandlers) {
      handlers.delete(handlerId);
    }
  }

  /**
   * Invoke a method on the SignalR hub
   * @param {string} methodName - The method to invoke
   * @param  {...any} args - Arguments to pass
   */
  async invoke(methodName, ...args) {
    await this._ensureConnected();
    return await this.connection.invoke(methodName, ...args);
  }

  /**
   * Make sure the connection is usable before invoking a hub method.
   * this.connection is assigned before start() resolves, so checking the
   * isConnected flag alone can send us into start() while the connection is
   * still Connecting - which throws "Cannot start a HubConnection that is not
   * in the 'Disconnected' state."
   * @private
   */
  async _ensureConnected() {
    if (this.connected) {
      return;
    }

    // A connect() started elsewhere (NavBar) may still be in flight - wait for it
    if (this.isConnecting && this.connectionPromise !== null) {
      try {
        await this.connectionPromise;
      } catch (error) {
        this.logError('Pending connection failed:', error);
      }
      if (this.connected) {
        return;
      }
    }

    if (!this.connection) {
      throw new Error('SignalR not connected');
    }

    // withAutomaticReconnect is already retrying - let it finish, don't fight it
    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      throw new Error(`SignalR not connected (state: ${this.getState()})`);
    }

    try {
      await this.connection.start();
      this.isConnected = true;
      this._registerPendingHandlers();
      this._startPing();
    } catch (error) {
      this.logError('Reconnection failed:', error);
      throw new Error('SignalR not connected and reconnection failed');
    }
  }

  /**
   * Get connection state
   * @returns {string}
   */
  getState() {
    if (!this.connection) {
      return 'Disconnected';
    }
    return signalR.HubConnectionState[this.connection.state];
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  get connected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get connection ID
   * @returns {string|null}
   */
  get connectionId() {
    return this.connection?.connectionId || null;
  }

  /**
   * Internal method to invoke all registered handlers for an event
   * @private
   */
  _invokeHandlers(eventName, ...args) {
    if (this.eventHandlers.has(eventName)) {
      for (const [handlerId, callback] of this.eventHandlers.get(eventName)) {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in SignalR event handler [${eventName}][${handlerId}]:`, error);
        }
      }
    }
  }

  /**
   * Internal method to notify connection lifecycle handlers
   * @private
   */
  _notifyHandlers(eventType, data) {
    if (this.eventHandlers.has(`_${eventType}`)) {
      for (const [, callback] of this.eventHandlers.get(`_${eventType}`)) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in lifecycle handler [${eventType}]:`, error);
        }
      }
    }
  }

  /**
   * Register event handlers on the connection after it's established
   * Called internally when connection is created
   */
  _registerPendingHandlers() {
    if (!this.connection) return;

    for (const [eventName] of this.eventHandlers) {
      this._bindEvent(eventName);
    }
  }

  /**
   * Attach the one dispatcher for an event to the current connection.
   * connection.on() APPENDS a handler, it never replaces one, so calling it twice
   * for the same event makes every component callback run twice - which is how a
   * chat message ends up rendered twice after a reconnect. off() first keeps this
   * idempotent no matter how many times the connection is restarted.
   * Names starting with '_' are lifecycle events dispatched by _notifyHandlers,
   * not hub methods, so they are never bound to the connection.
   * @private
   */
  _bindEvent(eventName) {
    if (!this.connection || eventName.startsWith('_')) {
      return;
    }

    this.connection.off(eventName);
    this.connection.on(eventName, (...args) => {
      this._invokeHandlers(eventName, ...args);
    });
  }
}

// Export singleton instance
const signalRService = new SignalRService();
export default signalRService;
